import { z } from 'zod';
import { workflowStageSchema } from '../constants/stages.js';
import { nonNegativePaiseSchema, paiseSchema } from '../money/paise.js';
import { paymentMethodSchema, type PaymentMethod } from '../money/payment-methods.js';
import {
  dateInputSchema,
  isoDateStringSchema,
  objectIdSchema,
  paginationQuerySchema,
} from './common.js';

/**
 * Money received against an order — as a LEDGER, not as a field.
 *
 * The one decision this file exists to encode: an entry is written once and never changed.
 * There is no update shape and no delete shape below, and there will not be one. A shop takes
 * ₹2000 as an advance, ₹500 more a week later, the balance at delivery, sometimes from three
 * different people at three different counters — and the argument that follows six weeks later
 * is settled by what the trail SAYS HAPPENED, not by what somebody later decided it should
 * have said. A wrong entry is corrected by a REVERSAL that points at it, so both the mistake
 * and the correction remain visible.
 *
 * The order's `amountPaidPaise` is the running sum of these entries and is derived from them
 * inside the same transaction that writes one, so the two can never disagree.
 *
 * Three kinds, because a shop means three different things:
 *
 *  - `payment`   money in. The ordinary case.
 *  - `refund`    money back OUT to the customer — an overpayment returned, a cancelled
 *                garment settled. It is its own kind rather than a negative payment so
 *                "what did we take today?" and "what did we give back today?" are separate
 *                numbers in the day book instead of one netted figure that hides both.
 *  - `reversal`  a correction of a specific earlier entry, carrying a reason and that entry's
 *                id. Never used for a refund: reversing means "this did not happen", refunding
 *                means "it happened and we gave it back".
 */

export const PAYMENT_KINDS = ['payment', 'refund', 'reversal'] as const;
export type PaymentKind = (typeof PAYMENT_KINDS)[number];
export const paymentKindSchema = z.enum(PAYMENT_KINDS);

export const PAYMENT_REFERENCE_MAX_LENGTH = 120;
export const PAYMENT_NOTE_MAX_LENGTH = 500;
export const REVERSAL_REASON_MAX_LENGTH = 500;

/**
 * What an entry of `kind` does to what the shop has been paid. Money in is positive; money
 * back out is negative. A reversal's effect is the negation of the entry it reverses, which
 * only the server can know, so it is not derivable here.
 */
export function paymentEffectPaise(kind: 'payment' | 'refund', amountPaise: number): number {
  return kind === 'payment' ? amountPaise : -amountPaise;
}

/**
 * The two numbers a counter actually needs, kept apart on purpose.
 *
 * `outstandingPaise` is what to ask for and never goes below zero; `creditPaise` is what the
 * shop is holding beyond the bill. Collapsing them into one signed "balance" is how a screen
 * ends up asking a customer for minus four hundred rupees.
 */
export function summariseOrderMoney(
  totalPaise: number,
  paidPaise: number,
): { outstandingPaise: number; creditPaise: number } {
  return {
    outstandingPaise: Math.max(0, totalPaise - paidPaise),
    creditPaise: Math.max(0, paidPaise - totalPaise),
  };
}

// --- One entry --------------------------------------------------------------------------------

export const paymentSchema = z.object({
  id: objectIdSchema,
  /**
   * What the money was against: an order, or an alteration (a walk-in has no order). Exactly one
   * of the two pairs is set, and the other is null.
   */
  orderId: objectIdSchema.nullable(),
  /** Denormalised so the day book reads without a join per row. */
  orderNumber: z.string().nullable(),
  alterationId: objectIdSchema.nullable(),
  alterationNumber: z.string().nullable(),
  customerId: objectIdSchema,
  customerName: z.string(),
  customerPhone: z.string(),
  kind: paymentKindSchema,
  /** Always positive, exactly as it was entered. The direction lives in `kind`. */
  amountPaise: nonNegativePaiseSchema,
  /** What this entry did to the order's paid amount: positive in, negative out. */
  signedAmountPaise: paiseSchema,
  method: paymentMethodSchema,
  /** When the shop received it, which is not always when it was typed in. */
  receivedAt: isoDateStringSchema,
  /** A UPI transaction id, a cheque number — whatever ties this to the other side's record. */
  reference: z.string().nullable(),
  note: z.string().nullable(),
  /** Who took the money. Captured at the time; a staff member who leaves does not erase it. */
  recordedByUserId: z.string().nullable(),
  recordedByName: z.string().nullable(),
  /** Set on a reversal: the entry it cancels. */
  reversesPaymentId: objectIdSchema.nullable(),
  /**
   * Set on an entry that HAS been reversed — derived from the reversal that points at it, not
   * stored on it, because the entry itself is never written to twice.
   */
  reversedByPaymentId: objectIdSchema.nullable(),
  reversedAt: isoDateStringSchema.nullable(),
  /** Why it was reversed, on the reversal entry. */
  reason: z.string().nullable(),
  createdAt: isoDateStringSchema,
});
export type Payment = z.infer<typeof paymentSchema>;

// --- Recording ---------------------------------------------------------------------------------

export const recordPaymentSchema = z.object({
  /** At least one paisa: a zero-rupee payment is a mis-tap, not a ledger entry. */
  amountPaise: paiseSchema.min(1, 'Enter an amount'),
  method: paymentMethodSchema,
  /** Defaults to now. A backdated entry is legitimate — money taken yesterday, typed today. */
  receivedAt: dateInputSchema.optional(),
  reference: z.string().trim().max(PAYMENT_REFERENCE_MAX_LENGTH).nullish(),
  note: z.string().trim().max(PAYMENT_NOTE_MAX_LENGTH).nullish(),
  kind: z.enum(['payment', 'refund']).default('payment'),
  /**
   * Taking more than the bill is real — a customer rounds up, or pays for work not yet added —
   * but it must be a decision somebody made rather than a typo that silently becomes a credit.
   * Without this flag the API refuses and says by how much.
   */
  allowOverpayment: z.boolean().default(false),
});
export type RecordPaymentInput = z.input<typeof recordPaymentSchema>;

export const reversePaymentSchema = z.object({
  /** Required. "Which entry was reversed, and why" is the whole point of reversing. */
  reason: z
    .string()
    .trim()
    .min(3, 'Say why this entry is being reversed')
    .max(REVERSAL_REASON_MAX_LENGTH),
});
export type ReversePaymentInput = z.infer<typeof reversePaymentSchema>;

/**
 * The advance taken while booking, so the counter interaction is one conversation rather than
 * "book it, now open it again, now record the money". It is recorded in the SAME transaction as
 * the order, so a booking that fails takes its advance with it.
 */
export const bookingAdvanceSchema = z.object({
  amountPaise: paiseSchema.min(1, 'Enter an amount'),
  method: paymentMethodSchema,
  reference: z.string().trim().max(PAYMENT_REFERENCE_MAX_LENGTH).nullish(),
  note: z.string().trim().max(PAYMENT_NOTE_MAX_LENGTH).nullish(),
});
export type BookingAdvanceInput = z.input<typeof bookingAdvanceSchema>;

// --- One order's money -------------------------------------------------------------------------

export const orderPaymentsSchema = z.object({
  orderId: objectIdSchema,
  orderNumber: z.string(),
  customerName: z.string(),
  customerPhone: z.string(),
  totalPaise: nonNegativePaiseSchema,
  /** The sum of the entries below. Never typed in, never patched. */
  paidPaise: paiseSchema,
  outstandingPaise: nonNegativePaiseSchema,
  creditPaise: nonNegativePaiseSchema,
  /**
   * What this shop reaches for most: the method on the majority of its recent payments. The
   * collection dialog opens on it, because a cash shop should not pick "cash" forty times a day.
   */
  suggestedMethod: paymentMethodSchema,
  entries: z.array(paymentSchema),
});
export type OrderPayments = z.infer<typeof orderPaymentsSchema>;

/** What a record or a reversal answers with: the entry that was written, and the new picture. */
export const paymentResultSchema = z.object({
  recorded: paymentSchema,
  payments: orderPaymentsSchema,
});
export type PaymentResult = z.infer<typeof paymentResultSchema>;

// --- The day book -------------------------------------------------------------------------------

export const dayBookQuerySchema = z.object({
  /** The shop's day, in its own timezone. Defaults to today. */
  date: dateInputSchema.optional(),
});
export type DayBookQuery = z.infer<typeof dayBookQuerySchema>;

export const methodTotalSchema = z.object({
  method: paymentMethodSchema,
  /** Money in through this method. */
  inPaise: nonNegativePaiseSchema,
  /** Money back out through it — refunds and reversals. */
  outPaise: nonNegativePaiseSchema,
  netPaise: paiseSchema,
  count: z.number().int().nonnegative(),
});
export type MethodTotal = z.infer<typeof methodTotalSchema>;

export const dayBookTotalsSchema = z.object({
  receivedPaise: nonNegativePaiseSchema,
  refundedPaise: nonNegativePaiseSchema,
  /** Received minus refunded and reversed — what the day actually added to the books. */
  netPaise: paiseSchema,
  /** What should be in the drawer. */
  cashPaise: paiseSchema,
  /** What should be in the bank. */
  digitalPaise: paiseSchema,
  /** Cheques, which are in neither until they clear. */
  chequePaise: paiseSchema,
  entryCount: z.number().int().nonnegative(),
});
export type DayBookTotals = z.infer<typeof dayBookTotalsSchema>;

export const dayBookSchema = z.object({
  /** Start of the shop's day, inclusive. */
  from: isoDateStringSchema,
  /** Start of the next day, exclusive. */
  to: isoDateStringSchema,
  entries: z.array(paymentSchema),
  totals: dayBookTotalsSchema,
  byMethod: z.array(methodTotalSchema),
});
export type DayBook = z.infer<typeof dayBookSchema>;

// --- Who still owes ------------------------------------------------------------------------------

export const OUTSTANDING_SORTS = ['age', 'amount'] as const;
export type OutstandingSort = (typeof OUTSTANDING_SORTS)[number];

export const outstandingQuerySchema = paginationQuerySchema.extend({
  /** Oldest debt first by default, which is the order a shop chases in. */
  sortBy: z.enum(OUTSTANDING_SORTS).default('age'),
  q: z.string().trim().max(120).optional(),
});
export type OutstandingQuery = z.infer<typeof outstandingQuerySchema>;

/**
 * One debt: an order's balance, or an alteration's (a walk-in has no order to owe on). Exactly one
 * of the two id/number pairs is set.
 */
export const outstandingRowSchema = z.object({
  orderId: objectIdSchema.nullable(),
  orderNumber: z.string().nullable(),
  alterationId: objectIdSchema.nullable(),
  alterationNumber: z.string().nullable(),
  customerId: objectIdSchema,
  customerName: z.string(),
  /** Chasing a balance is a phone call, so the number travels with the row. */
  customerPhone: z.string(),
  bookedAt: isoDateStringSchema,
  promisedDate: isoDateStringSchema.nullable(),
  /** The order's stage; null on an alteration, which has its own short lifecycle. */
  stage: workflowStageSchema.nullable(),
  totalPaise: nonNegativePaiseSchema,
  paidPaise: paiseSchema,
  outstandingPaise: nonNegativePaiseSchema,
  /** Days since booking — the age the list is sorted by. */
  ageDays: z.number().int().nonnegative(),
  lastPaymentAt: isoDateStringSchema.nullable(),
});
export type OutstandingRow = z.infer<typeof outstandingRowSchema>;

export const outstandingPageSchema = z.object({
  items: z.array(outstandingRowSchema),
  page: z.number().int(),
  pageSize: z.number().int(),
  total: z.number().int(),
  /** Every unpaid rupee in the shop, not just this page's. */
  totalOutstandingPaise: nonNegativePaiseSchema,
});
export type OutstandingPage = z.infer<typeof outstandingPageSchema>;

/** The methods that make sense on a refund, in the order a shop offers them. */
export const REFUNDABLE_METHODS: readonly PaymentMethod[] = Object.freeze([
  'cash',
  'upi',
  'bank_transfer',
  'card',
  'cheque',
]);
